import React, { useState } from 'react';
import { usePortfolio } from '../../../hooks/usePortfolio';
import { supabase } from '../../../lib/supabase';

export default function AdminPortfolio() {
  const { portfolioItems, loading, refreshPortfolio } = usePortfolio();

  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '',
    category: '',
    description: '',
    tags: ''
  });

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);

  const uploadFile = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
    const filePath = fileName;

    const { error: uploadError } = await supabase.storage
      .from('portfolio')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('portfolio').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleDelete = async (id: string | number) => {
    if (!confirm('Tem certeza que deseja excluir este item?')) return;

    const { error } = await supabase.from('portfolio').delete().eq('id', id);

    if (error) {
      alert('Erro ao deletar: ' + error.message);
    } else {
      refreshPortfolio();
    }
  };

  const handleSave = async () => {
    if (!coverFile) {
      alert('Selecione a imagem principal.');
      return;
    }

    setSaving(true);

    try {
      const coverUrl = await uploadFile(coverFile);

      const galleryUrls: string[] = [];
      for (const file of galleryFiles) {
        const url = await uploadFile(file);
        galleryUrls.push(url);
      }

      const { error } = await supabase.from('portfolio').insert([
        {
          title: form.title,
          category: form.category,
          description: form.description,
          image: coverUrl,
          images: [coverUrl, ...galleryUrls],
          tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        }
      ]);

      if (error) throw error;

      alert('Item salvo com sucesso!');

      setIsAdding(false);
      setForm({
        title: '',
        category: '',
        description: '',
        tags: ''
      });
      setCoverFile(null);
      setGalleryFiles([]);

      refreshPortfolio();
    } catch (error: any) {
      alert('Erro: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Portfólio</h1>
          <p className="text-gray-600">Visualize e edite os trabalhos realizados.</p>
        </div>

        <button
          onClick={() => setIsAdding(true)}
          className="bg-[#1e3a8a] text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <i className="ri-add-line"></i> Novo Item
        </button>
      </div>

      {isAdding && (
        <div className="mb-8 bg-white p-6 rounded-xl shadow border">
          <h2 className="text-xl font-bold mb-4">Novo Item</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              placeholder="Título"
              className="border p-2 rounded"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />

            <input
              placeholder="Categoria"
              className="border p-2 rounded"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />

            <textarea
              placeholder="Descrição"
              className="border p-2 rounded md:col-span-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <input
              placeholder="Tags (separadas por vírgula)"
              className="border p-2 rounded md:col-span-2"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />

            <div className="border rounded p-3 bg-white md:col-span-2">
              <label className="block font-semibold mb-2">Imagem principal</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              />
              {coverFile && (
                <p className="text-sm text-gray-600 mt-2">{coverFile.name}</p>
              )}
            </div>

            <div className="border rounded p-3 bg-white md:col-span-2">
              <label className="block font-semibold mb-2">Galeria de imagens</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
              />
              {galleryFiles.length > 0 && (
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  {galleryFiles.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 text-white px-4 py-2 rounded"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>

            <button
              onClick={() => setIsAdding(false)}
              className="bg-gray-300 px-4 py-2 rounded"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">
          Carregando portfólio...
        </div>
      ) : portfolioItems.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          Nenhum item cadastrado.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item: any) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <img
                src={item.image || ''}
                alt={item.title || 'Item do portfólio'}
                className="w-full h-48 object-cover"
              />

              <div className="p-4">
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                  {item.category || 'Sem categoria'}
                </span>

                <h3 className="text-lg font-bold text-gray-900 mt-1">
                  {item.title || 'Sem título'}
                </h3>

                <p className="text-sm text-gray-600 mt-2">
                  {item.description || ''}
                </p>

                {Array.isArray(item.images) && item.images.length > 1 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {item.images.map((img: string, index: number) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Galeria ${index + 1}`}
                        className="w-16 h-16 object-cover rounded border"
                      />
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xl"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}